import { z } from 'zod'

// ===== Category Schemas =====
export const InventoryCategorySchema = z.object({
  _id: z.string(),
  name: z.string().min(1, 'Category name is required'),
  description: z.string().optional(),
  active: z.boolean(),
  createdAt: z.string().optional(),
  createdBy: z.string().optional(),
  updatedAt: z.string().optional(),
  updatedBy: z.string().optional(),
})

export const InventoryCategoryCreateSchema = z.object({
  name: z.string().min(1, 'Category name is required'),
  description: z.string().optional(),
})

export const InventoryCategoryUpdateSchema =
  InventoryCategoryCreateSchema.extend({
    _id: z.string().min(1, 'Category ID is required'),
    active: z.boolean(),
  })

// ===== Inventory Stock Schemas =====
export const InventoryStockSchema = z.object({
  _id: z.string(),
  churchId: z.string().min(1, 'Church ID is required'),
  categoryId: z.string().min(1, 'Category ID is required'),
  item: z.string().min(1, 'Item name is required'),
  brand: z.string().optional(),
  unit: z.string().min(1, 'Unit is required'),
  quantity: z.number().min(0, 'Quantity cannot be negative'),
  location: z.string().optional(),
  minThreshold: z.number().min(0).optional(),
  createdAt: z.string().optional(),
  createdBy: z.string().optional(),
  updatedAt: z.string().optional(),
  updatedBy: z.string().optional(),
})

export const InventoryStockCreateSchema = z.object({
  itemId: z.string().min(1, 'Item ID is required'),
  churchId: z.string().min(1, 'Church ID is required'),
  categoryId: z.string().min(1, 'Category ID is required'),
  item: z.string().min(1, 'Item name is required'),
  brand: z.string().optional(),
  unit: z.string().min(1, 'Unit is required'),
  quantity: z.number().min(0, 'Quantity cannot be negative').default(0),
  location: z.string().optional(),
  minThreshold: z.number().min(0).optional(),
})

export const InventoryStockUpdateSchema = z.object({
  _id: z.string().min(1, 'Stock ID is required'),
  location: z.string().optional(),
  minThreshold: z.number().min(0).optional(),
})

// ===== Transaction Schemas =====
const AcquisitionSchema = z.object({
  price: z.number().min(0, 'Price cannot be negative'),
  purchaseDate: z.string().min(1, 'Purchase date is required'),
  invoiceNumber: z.string().optional(),
  invoiceUrl: z.string().optional(), // URL to the stored invoice file
  provider: z.string().optional(),
})

const DisposalSchema = z.object({
  reason: z.enum(['DAMAGE', 'GIFT', 'SALE', 'LOSS', 'USAGE']),
  authorizedBy: z.string().optional(),
})

export const InventoryTransactionSchema = z.object({
  _id: z.string(),
  type: z.enum(['IN', 'OUT']),
  churchId: z.string().min(1, 'Church ID is required'),
  categoryId: z.string().min(1, 'Category ID is required'),
  item: z.string().min(1, 'Item name is required'),
  brand: z.string().optional(),
  description: z.string().optional(),
  unit: z.string().min(1, 'Unit is required'),
  itemId: z.string().min(1, 'Item ID is required'),
  quantity: z.number().min(1, 'Quantity must be at least 1'),
  acquisition: AcquisitionSchema.optional(),
  disposal: DisposalSchema.optional(),
  notes: z.string().optional(),
  inventoryStockId: z.string().optional(),
  createdAt: z.string(),
  createdBy: z.string().optional(),
  updatedAt: z.string().optional(),
  updatedBy: z.string().optional(),
})

// IN Transaction (Add items)
export const InventoryInTransactionCreateSchema = z.object({
  type: z.literal('IN'),
  churchId: z.string().min(1, 'Church ID is required'),
  categoryId: z.string().min(1, 'Category ID is required'),
  item: z.string().min(1, 'Item name is required'),
  brand: z.string().optional(),
  description: z.string().optional(),
  unit: z.string().min(1, 'Unit is required'),
  quantity: z.number().min(1, 'Quantity must be at least 1'),
  acquisition: AcquisitionSchema,
  inventoryStockId: z.string().optional(),
  notes: z.string().optional(),
})

// OUT Transaction (Remove items)
export const InventoryOutTransactionCreateSchema = z.object({
  type: z.literal('OUT'),
  churchId: z.string().min(1, 'Church ID is required'),
  categoryId: z.string().min(1, 'Category ID is required'),
  item: z.string().min(1, 'Item name is required'),
  brand: z.string().optional(),
  description: z.string().optional(),
  unit: z.string().min(1, 'Unit is required'),
  quantity: z.number().min(1, 'Quantity must be at least 1'),
  disposal: DisposalSchema,
  notes: z.string().optional(),
  inventoryStockId: z.string().optional(),
})

// ===== Type Exports =====
export type InventoryCategoryData = z.infer<typeof InventoryCategorySchema>
export type InventoryCategoryCreateData = z.infer<
  typeof InventoryCategoryCreateSchema
>
export type InventoryCategoryUpdateData = z.infer<
  typeof InventoryCategoryUpdateSchema
>

export type InventoryStockData = z.infer<typeof InventoryStockSchema>
export type InventoryStockCreateData = z.infer<
  typeof InventoryStockCreateSchema
>
export type InventoryStockUpdateData = z.infer<
  typeof InventoryStockUpdateSchema
>

export type InventoryTransactionData = z.infer<
  typeof InventoryTransactionSchema
>
export type InventoryInTransactionCreateData = z.infer<
  typeof InventoryInTransactionCreateSchema
>
export type InventoryOutTransactionCreateData = z.infer<
  typeof InventoryOutTransactionCreateSchema
>
